<template>
  <command v-if="wpsClient.token.allowOffline" @click="trigger">
    <slot
      v-bind="{
        disabled: offlineStore.offlineActive || offlineStore.offlineLoading,
        icon: icon,
        label: label,
        toolTip: label,
      }"
    />
  </command>
</template>
<script setup lang="ts">
import { computed, toValue } from "vue";
import { useI18n } from "vue-i18n";

import { useOfflineStore } from "@/stores/offlineState";

import { posthogKeys } from "@/helpers/constants";
import { usePostHog } from "@/helpers/posthog";
import { useUILayers } from "@/components/UILayers";

import OfflineRetryToastContent from "./OfflineRetryToastContent.vue";
import Command from "../command";

import { useNavigator } from "@/components/navigator";
import { useWpsClient } from "@/wps/client";
import { useUiVisibilityStore } from "@/stores/uiVisibility";
import { useLiveAnnouncer } from "@/services/composables/useLiveAnnouncer";

const { capture: capturePosthogEvent } = usePostHog();

const offlineStore = useOfflineStore();
const visibilityStore = useUiVisibilityStore();

const wpsClient = useWpsClient();

const { t } = useI18n();

const { showToast } = useUILayers();

const icon = computed<string>(() => {
  if (offlineStore.offlineLoading) {
    return "downloading";
  } else if (offlineStore.offlineActive) {
    return "done_all";
  } else {
    return "cloud_off";
  }
});

const label = computed<string>(() => {
  if (offlineStore.offlineLoading) {
    return t("moreOptions.offlineLoading");
  } else if (offlineStore.offlineActive) {
    return t("moreOptions.offlineActive");
  } else {
    return t("moreOptions.offline");
  }
});

const navigator = useNavigator();

async function trigger() {
  //Set a small delay to ensure the UI is updated after the options menu is closed by the UILayer
  setTimeout(() => visibilityStore.setShowUI(true), 200);
  if (offlineStore.offlineLoading || offlineStore.offlineActive) {
    return;
  }
  useLiveAnnouncer.announce("politeStatus", t("moreOptions.offlineLoading"));

  const success = await offlineStore.goOffline(toValue(navigator));

  if (success) {
    capturePosthogEvent(posthogKeys.offlineMode);
    visibilityStore.setShowUI(true);
    useLiveAnnouncer.announce("politeStatus", t("moreOptions.offlineActive"));
  } else {
    showToast(
      OfflineRetryToastContent,
      {
        publicationTitle:
          wpsClient.publication?.metadata.title.getTranslation("en"),
        onRetry: trigger,
      },
      "feedback",
      { timeout: 5000 }
    );
  }
}
</script>
